import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Card className="border-red-200 bg-red-50 max-w-lg mx-auto my-8">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <CardTitle className="text-red-700">Something went wrong</CardTitle>
            </div>
            <CardDescription className="text-red-600">
              An error occurred in this component
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-red-800 font-mono bg-red-100 p-3 rounded overflow-auto max-h-40">
              {this.state.error?.toString()}
            </div>
            {this.state.errorInfo && (
              <details className="mt-2">
                <summary className="text-sm text-red-700 cursor-pointer">View component stack</summary>
                <div className="text-xs text-red-800 font-mono bg-red-100 p-3 rounded mt-2 overflow-auto max-h-40">
                  {this.state.errorInfo.componentStack}
                </div>
              </details>
            )}
          </CardContent>
          <CardFooter>
            <Button 
              variant="outline" 
              className="bg-white hover:bg-red-50 border-red-200 text-red-700"
              onClick={this.handleReset}
            >
              Try Again
            </Button>
          </CardFooter>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
